<template>
  <div>
    <BaseModal class="upload-modal" v-model="isOpenModal">
      <div class="file-uploader">
        <h2>
          {{ $t("message.uploads.upload_new_file_step", { step: step }) }}
        </h2>
        <div class="body" v-if="!processingFile">
          <div v-if="step == 0">
            <div class="base-inputs">
              <div>
                <input type="file" id="fileInput" @change="onFileChanged" />
              </div>
              <select
                class="default-select"
                name="file_type"
                id="file_selector"
                v-model="formValues.fileType"
                @change="LoadOtherOptions()"
              >
                <option value="">{{
                  $t("message.uploads.select_file_type")
                }}</option>
                <option
                  v-for="option in uploadOptions"
                  :value="option.value"
                  :key="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div>
              <br />
              <div v-html="description" />
            </div>
            <div
              v-if="otherFormOptions && formValues.otherOptions"
              class="other-inputs"
            >
              <template v-for="field in otherFormOptions">
                <div class="input" :key="field.inputId">
                  <DynamicFormField
                    :data="field"
                    v-model="formValues.otherOptions[field.inputId]"
                  />
                </div>
              </template>
            </div>
            <button
              id="upload-btn"
              class="btn-dark-button"
              @click="submitUploadFile()"
            >
              {{ $t("message.uploads.upload") }}
            </button>
            <div class="footer">
              {{ $t("message.uploads.category_pending") }}
            </div>
          </div>
          <div v-if="step == 1 && uploadedFields && requiredFields">
            <UploadFieldsMatcher
              :uploadedFields="uploadedFields"
              :requiredFields="requiredFields"
              :firstRow="firstRow"
              @confirmed="matchConfirmed"
            />
          </div>
          <div class="responses">
            <p
              v-for="(res, index) in fileUploadResponse"
              :key="index"
              v-html="res"
            />
            <ul v-if="UploadActions && UploadActions.length > 0">
              <li
                v-for="(res, index) in UploadActions"
                :key="index"
                v-html="res"
              />
            </ul>
            <ul>
              <template v-if="filesGenerated && filesGenerated.length > 0">
                <li v-for="(file, index) in filesGenerated" :key="index">
                  <button
                    class="btn-dark-button"
                    @click="downloadFile(file.route)"
                  >
                    {{
                      $t("message.uploads.download_file", { name: file.name })
                    }}
                  </button>
                </li>
              </template>
            </ul>
          </div>
        </div>
        <div v-else>
          <BaseSmallSpinner />
          <div v-if="polling" class="polling-status">
            <p>Processing... {{ pollSeconds }}s elapsed</p>
            <p v-if="progressTotal">
              {{ progressProcessed }} / {{ progressTotal }}
            </p>
            <p v-if="progressMessage">{{ progressMessage }}</p>
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>
<script>
import {
  signAndDownloadFile,
  getUploadOptions,
  uploadFileService,
  uploadFileServiceMatch,
  getUploadedFileInfo
} from "@/api/store/UploadFilesService.js";
import DynamicFormField from "@/components/shared/dynamicForm/DynamicFormField.vue";
import UploadFieldsMatcher from "@/components/shared/UploadFieldsMatcher.vue";
export default {
  components: {
    DynamicFormField,
    UploadFieldsMatcher
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    forceType: {
      type: [String, Boolean],
      required: false,
      default: false
    },
    allowedTypes: {
      type: Array,
      required: false,
      default: null
    }
  },
  data() {
    return {
      orderFull: null,
      processing: false,
      isOpenModal: this.value,
      uploadOptions: [],
      newFile: [],
      processingFile: false,
      formValues: {
        fileType: "",
        otherOptions: {}
      },
      otherFormOptions: null,
      fileUploadResponse: [],
      UploadActions: [],
      filesGenerated: [],
      step: 0,
      uploadedFields: [],
      requiredFields: [],
      firstRow: [],
      uploadedFileInProcess: null,
      polling: false,
      pollTimer: null,
      pollSeconds: 0,
      progressProcessed: null,
      progressTotal: null,
      progressMessage: null
    };
  },
  beforeDestroy() {
    this.stopPolling();
  },
  computed: {
    isOpen() {
      return this.value;
    },
    formValuesValid() {
      if (this.formValues.fileType != "") {
        return {
          valid: true,
          message: ""
        };
      } else {
        return {
          valid: false,
          message: this.$t("message.uploads.type_not_selected")
        };
      }
    },
    description() {
      if (this.formValues?.fileType) {
        let desc = this.uploadOptions.find(
          x => x.value == this.formValues.fileType
        );
        desc = desc.description || "";
        return desc;
      } else {
        return "";
      }
    }
  },
  watch: {
    isOpenModal(val) {
      this.$emit("input", val);
      this.step = 0;
    },
    value(val) {
      this.resetResponse();
      this.isOpenModal = val;
      this.step = 0;
    },
    formValues: {
      deep: true,
      handler() {
        this.fileUploadResponse = [];
      }
    }
  },
  mounted() {
    let options = getUploadOptions();
    if (this.allowedTypes && this.allowedTypes.length > 0) {
      options = options.filter(o => this.allowedTypes.includes(o.value));
    }
    this.uploadOptions = options;
  },
  methods: {
    resetResponse() {
      this.UploadActions = [];
      this.fileUploadResponse = [];
      this.filesGenerated = [];
      this.stopPolling();
      this.progressProcessed = null;
      this.progressTotal = null;
      this.progressMessage = null;
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.polling = false;
      this.pollSeconds = 0;
    },
    startPolling(uploadId) {
      this.polling = true;
      this.pollSeconds = 0;
      this.pollTimer = setInterval(async () => {
        this.pollSeconds += 3;
        try {
          const info = await getUploadedFileInfo(uploadId);
          this.progressProcessed = info.progressProcessed ?? null;
          this.progressTotal = info.progressTotal ?? null;
          this.progressMessage = info.progressMessage ?? null;
          if (info.status && info.status !== "processing") {
            this.stopPolling();
            const succeeded = info.status === "processed";
            const results = info.results || {};
            this.fileUploadResponse.push(results.message || `Upload ${info.status}`);
            this.UploadActions = results.actions || [];
            this.filesGenerated = results.filesGenerated || [];
            this.$toast.open({
              message: succeeded ? "Upload processed" : `Upload ${info.status}`,
              type: succeeded ? "success" : "error",
              position: "top-right"
            });
            this.processingFile = false;
            this.$emit("uploadedFile", info);
          }
        } catch (e) {
          // transient error — keep polling
        }
      }, 3000);
    },
    async downloadFile(key) {
      console.log("s3Path", key);
      const res = await signAndDownloadFile(key);
      console.log("res", res);
      if (!res) {
        this.$toast.open({
          message: `Error Downloading file`,
          type: "error",
          position: "top-right"
        });
      }
      return;
    },
    LoadOtherOptions() {
      this.formValues.otherOptions = {};
      this.otherFormOptions = null;
      const otherOptionsSet = this.uploadOptions.find(
        x => x.value == this.formValues.fileType
      );
      if (otherOptionsSet?.otherOptions) {
        this.otherFormOptions = otherOptionsSet.otherOptions;
        otherOptionsSet.otherOptions.forEach(t => {
          this.formValues.otherOptions[t.inputId] = null;
        });
      }
    },
    onFileChanged() {
      this.resetResponse();
      var fileList = [];
      for (var i = 0; i < event.target.files.length; i++) {
        fileList.push(event.target.files[i]);
      }
      this.newFile = fileList;
      this.fileUploadResponse = [];
      this.UploadActions = [];
    },
    submitUploadFile() {
      this.UploadActions = [];
      this.fileUploadResponse = [];
      this.processingFile = true;
      if (
        this.newFile &&
        this.newFile.length > 0 &&
        this.formValuesValid.valid
      ) {
        this.uploadFile(this.newFile)
          // eslint-disable-next-line no-unused-vars
          .then(res => {
            console.log("submit upload file completed");
            this.newFile = null;
            this.$emit("fileUploaded");
            this.processingFile = false;
          })
          .catch(() => {
            console.log("error on upload catch");
            this.processingFile = false;
          })
          .finally(() => {
            this.newFile = null;
          });
      } else {
        this.processingFile = false;
        if (!this.newFile || this.newFile.length == 0) {
          this.fileUploadResponse.push(
            this.$t("message.uploads.no_file_selected")
          );
        }
        if (!this.formValuesValid.valid) {
          this.fileUploadResponse.push(this.formValuesValid.message);
        }
        return;
      }
    },
    uploadFile(file) {
      return uploadFileService(file, this.formValues, this.step)
        .then(res => {
          if (res.nextStep && res.nextStep > this.step) {
            this.step = res.nextStep;
            this.uploadedFields = res.fileUploadedFields;
            this.requiredFields = res.requiredFields;
            this.firstRow = res.firstRow;
            this.uploadedFileInProcess = res.upload;
          } else if (res.status === "processing" && res.upload && res.upload.id) {
            this.processingFile = true;
            this.startPolling(res.upload.id);
          } else {
            const uploadedFile = res.upload || null;
            if (res.status == "success") {
              this.$toast.open({
                message: `${res.message}`,
                type: "success",
                position: "top-right"
              });
            }
            if (res.status == "error") {
              this.$toast.open({
                message: `${res.message}`,
                type: "error",
                position: "top-right"
              });
            }
            this.$emit("uploadedFile", uploadedFile);
            this.fileUploadResponse.push(`${res.message}`);
            this.UploadActions = res.actions || [];
            this.filesGenerated = res.filesGenerated || [];
            return res;
          }
        })
        .catch(e => {
          if (e.response) {
            this.fileUploadResponse.push(`${e.response}`);
          } else {
            this.fileUploadResponse.push(`Error Uploading file`);
          }
          throw e;
        });
    },
    matchConfirmed(match) {
      this.processingFile = true;
      uploadFileServiceMatch(this.uploadedFileInProcess, match, this.step)
        .then(res => {
          this.fileUploadResponse = [];
          this.uploadedFields = null;
          this.requiredFields = null;
          this.firstRow = null;
          this.uploadedFileInProcess = null;
          const uploadedFile = res.upload || null;
          this.step = 0;

          if (res.status === "processing" && res.upload && res.upload.id) {
            this.processingFile = true;
            this.startPolling(res.upload.id);
            return res;
          }

          if (res.status == "success") {
            this.$toast.open({
              message: `${res.message}`,
              type: "success",
              position: "top-right"
            });
          }
          if (res.status == "error") {
            this.$toast.open({
              message: `${res.message}`,
              type: "error",
              position: "top-right"
            });
          }
          this.$emit("uploadedFile", uploadedFile);
          this.fileUploadResponse.push(`${res.message}`);
          console.log("match res", res);
          this.UploadActions = res.actions || [];
          this.filesGenerated = res.filesGenerated || [];
          this.processingFile = false;
          return res;
        })
        .catch(e => {
          if (e.response) {
            this.fileUploadResponse.push(`${e.response}`);
          } else {
            this.fileUploadResponse.push(`Error Uploading file`);
          }
          this.processingFile = false;
          throw e;
        });
    }
  }
};
</script>
<style lang="scss">
.upload-modal {
  .modal-content {
    max-width: 550px;
    .file-uploader {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      .body {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        min-height: 250px;
        .base-inputs {
          display: grid;
          grid-gap: 20px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          #file_selector {
            width: 100%;
          }
        }
        .other-inputs {
          margin-top: 40px;
          display: grid;
          grid-gap: 20px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          div {
            display: flex;
          }
        }
      }
      .footer {
        width: 100%;
        text-align: left;
        font-size: 12px;
      }
      h2 {
        font-weight: 700;
        font-size: 20px;
        width: 100%;
        text-align: center;
      }
      #upload-btn {
        width: 80px;
        margin: 40px auto;
      }
      .input-box {
        width: 250px;
        margin: 20px auto;
        input {
          text-align: center;
        }
      }
    }
  }
}
</style>
